export const INLINE_IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp']);
export const INLINE_IMAGE_LIMIT = 10 * 1024 * 1024;

export function inlineImageUrl(value) {
  try {
    const url = new URL(String(value || ''));
    return url.protocol === 'https:' && !url.username && !url.password && !url.port && !url.hash
      && /^[a-z0-9-]+(?:\.[a-z0-9-]+)*\.(?:file\.force\.com|documentforce\.com)$/.test(url.hostname)
      && /^\/sfc\/dist\/version\/download(?:\/(?:[A-Za-z0-9_-]+\/?)?)?$/.test(url.pathname) ? url.href : '';
  } catch { return ''; }
}

export function clipboardImages(data) {
  if (!data) return [];
  const files = Array.from(data.files || []);
  if (!files.length) for (const item of Array.from(data.items || [])) {
    if (item.kind === 'file') {
      const file = item.getAsFile();
      if (file) files.push(file);
    }
  }
  return files.filter(file => /^image\//i.test(file.type));
}

export function cleanInlineImages(root) {
  root.querySelectorAll('[data-inline-upload]').forEach(node => node.remove());
  root.querySelectorAll('img').forEach(node => {
    const src = inlineImageUrl(node.getAttribute('src'));
    if (!src) { node.remove(); return; }
    const alt = node.getAttribute('alt') || 'Pasted image';
    const width = Math.min(16000, Math.max(1, parseInt(node.getAttribute('width'), 10) || 640));
    const height = Math.min(16000, Math.max(1, parseInt(node.getAttribute('height'), 10) || 360));
    Array.from(node.attributes).forEach(attribute => node.removeAttribute(attribute.name));
    node.setAttribute('src', src);
    node.setAttribute('alt', alt.slice(0, 180));
    node.setAttribute('width', String(width));
    node.setAttribute('height', String(height));
    node.setAttribute('loading', 'lazy');
    node.setAttribute('referrerpolicy', 'no-referrer');
  });
}

export function hasInlineImage(html) {
  const template = document.createElement('template');
  template.innerHTML = String(html || '');
  return Array.from(template.content.querySelectorAll('img')).some(img => inlineImageUrl(img.getAttribute('src')));
}

export function inlineFilesForHtml(files, html) {
  const template = document.createElement('template');
  template.innerHTML = String(html || '');
  const urls = new Set(Array.from(template.content.querySelectorAll('img')).map(img => inlineImageUrl(img.getAttribute('src'))).filter(Boolean));
  return files.filter(file => urls.has(inlineImageUrl(file.contentDownloadUrl)));
}

export function inlineEditorHtml(editor) {
  const copy = editor.cloneNode(true);
  cleanInlineImages(copy);
  return copy.innerHTML;
}

function imageDimensions(file, signal) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const url = URL.createObjectURL(file);
    let timer;
    const finish = (error) => {
      clearTimeout(timer);
      signal.removeEventListener('abort', aborted);
      URL.revokeObjectURL(url);
      image.onload = image.onerror = null;
      if (error) reject(error);
      else if (!image.naturalWidth || !image.naturalHeight || image.naturalWidth > 16000 || image.naturalHeight > 16000
        || image.naturalWidth * image.naturalHeight > 40000000) reject(new Error('Choose an image smaller than 40 megapixels.'));
      else resolve({ width: image.naturalWidth, height: image.naturalHeight });
    };
    const aborted = () => finish(new Error('Image paste cancelled.'));
    image.onload = () => finish();
    image.onerror = () => finish(new Error('This image could not be read. Choose a PNG, JPEG, GIF or WebP image.'));
    timer = setTimeout(() => finish(new Error('Reading this image timed out. Retry or remove it.')), 20000);
    signal.addEventListener('abort', aborted, { once: true });
    if (signal.aborted) { aborted(); return; }
    image.src = url;
  });
}

// A placeholder belongs to one editor and one upload. Never serialize transient
// nodes or replace the whole editor when an asynchronous upload finishes.
export function createInlineImagePaste(editor, options) {
  const tasks = new Map();
  let disposed = false;
  const warnBeforeLeaving = event => {
    if (!tasks.size) return;
    event.preventDefault(); event.returnValue = '';
  };
  window.addEventListener('beforeunload', warnBeforeLeaving);
  const notify = () => {
    if (disposed) return;
    editor.dataset.inlineImagesPending = String(tasks.size);
    options.onState?.({ pending: tasks.size, failed: [...tasks.values()].filter(task => task.error).length });
  };
  const emit = () => { if (!disposed) options.onChange?.(inlineEditorHtml(editor)); };
  const current = task => !disposed && tasks.get(task.id) === task && editor.contains(task.node) && !task.abort.signal.aborted;
  const progress = (task, percent, label) => {
    if (!current(task)) return;
    task.progress.value = Math.min(99, Math.max(0, Number(percent) || 0));
    task.label.textContent = label || `Uploading ${task.file.name} (${Math.round(task.progress.value)}%)`;
  };
  const remove = task => {
    tasks.delete(task.id);
    task.abort.abort();
    task.node.remove();
    notify(); emit();
  };
  const run = async task => {
    if (!current(task) || task.running) return;
    task.running = true;
    task.error = null;
    task.node.classList.remove('inline-image-upload--failed');
    task.node.setAttribute('aria-busy', 'true');
    task.retry.hidden = true;
    notify();
    try {
      if (!INLINE_IMAGE_TYPES.has(task.file.type.toLowerCase())) throw new Error('Use PNG, JPEG, GIF or WebP. SVG images are not supported.');
      if (!task.file.size || task.file.size > INLINE_IMAGE_LIMIT) throw new Error('Choose an image between 1 byte and 10 MB.');
      task.dimensions ||= await imageDimensions(task.file, task.abort.signal);
      if (!current(task)) return;
      task.node.style.width = `${task.dimensions.width}px`;
      task.node.style.aspectRatio = `${task.dimensions.width} / ${task.dimensions.height}`;
      progress(task, 0);
      task.result ||= await options.upload(task.file, {
        signal: task.abort.signal,
        onProgress: percent => progress(task, percent),
        transfer: task.transfer,
      });
      if (!current(task)) return;
      const src = inlineImageUrl(task.result?.contentDownloadUrl);
      if (!src) {
        task.result = null;
        throw new Error('Salesforce did not return a direct image link. Retry to finish attaching this image.');
      }
      const image = document.createElement('img');
      image.alt = task.file.name;
      image.width = task.dimensions.width;
      image.height = task.dimensions.height;
      image.referrerPolicy = 'no-referrer';
      progress(task, 99, 'Loading image...');
      await new Promise((resolve, reject) => {
        const timer = setTimeout(() => finish(new Error('The image link is not ready. Retry without uploading it again.')), 25000);
        const aborted = () => finish(new Error('Image paste cancelled.'));
        const finish = error => {
          clearTimeout(timer);
          task.abort.signal.removeEventListener('abort', aborted);
          image.onload = image.onerror = null;
          error ? reject(error) : resolve();
        };
        image.onload = () => finish();
        image.onerror = () => finish(new Error('The image link could not load. Retry without uploading it again.'));
        task.abort.signal.addEventListener('abort', aborted, { once: true });
        image.src = src;
      });
      if (!current(task)) return;
      options.onUploaded?.(task.result);
      const selection = window.getSelection();
      const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
      const caretAfterImage = range?.collapsed && range.startContainer === task.node.parentNode
        && range.startOffset === Array.prototype.indexOf.call(task.node.parentNode.childNodes, task.node) + 1;
      task.node.replaceWith(image);
      if (caretAfterImage) {
        range.setStartAfter(image); range.collapse(true);
        selection.removeAllRanges(); selection.addRange(range);
      }
      tasks.delete(task.id);
      notify(); emit();
    } catch (error) {
      if (!current(task)) return;
      task.error = error.message || 'The image could not be uploaded.';
      task.node.classList.add('inline-image-upload--failed');
      task.node.setAttribute('aria-busy', 'false');
      task.label.textContent = task.error;
      task.retry.hidden = false;
      notify();
    } finally { task.running = false; }
  };
  return {
    get pending() { return tasks.size; },
    paste(event) {
      const files = clipboardImages(event.clipboardData);
      if (disposed || !files.length || editor.getAttribute('contenteditable') === 'false') return false;
      event.preventDefault();
      event.stopPropagation();
      const selection = window.getSelection();
      const range = selection?.rangeCount && editor.contains(selection.getRangeAt(0).commonAncestorContainer)
        ? selection.getRangeAt(0).cloneRange() : document.createRange();
      if (!editor.contains(range.commonAncestorContainer)) { range.selectNodeContents(editor); range.collapse(false); }
      const fragment = document.createDocumentFragment();
      const added = files.map(file => {
        const id = `paste-${window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`}`;
        const node = document.createElement('span');
        node.className = 'inline-image-upload';
        node.dataset.inlineUpload = id;
        node.contentEditable = 'false';
        node.setAttribute('role', 'group');
        node.setAttribute('aria-label', `Image upload: ${file.name}`);
        node.style.width = '320px'; node.style.aspectRatio = '16 / 9';
        const label = document.createElement('span'); label.className = 'inline-image-upload__label';
        label.setAttribute('role', 'status'); label.textContent = 'Reading image...';
        const meter = document.createElement('progress'); meter.max = 100; meter.value = 0;
        meter.setAttribute('aria-label', `Upload progress: ${file.name}`);
        const controls = document.createElement('span'); controls.className = 'inline-image-upload__controls';
        const retry = document.createElement('button'); retry.type = 'button'; retry.textContent = 'Retry'; retry.hidden = true;
        const discard = document.createElement('button'); discard.type = 'button'; discard.textContent = 'Remove';
        discard.setAttribute('aria-label', `Remove pasted image ${file.name}`);
        controls.append(retry, discard); node.append(label, meter, controls); fragment.append(node, document.createElement('br'));
        const task = { id, file, node, label, progress: meter, retry, abort: new AbortController(), transfer: {} };
        retry.onclick = event => { event.preventDefault(); event.stopPropagation(); run(task); };
        discard.onclick = event => { event.preventDefault(); event.stopPropagation(); remove(task); };
        tasks.set(id, task);
        return task;
      });
      range.deleteContents(); range.insertNode(fragment);
      range.setStartAfter(added[added.length - 1].node); range.collapse(true);
      selection?.removeAllRanges(); selection?.addRange(range);
      notify(); emit();
      added.forEach(run);
      return true;
    },
    reconcile() {
      for (const task of tasks.values()) if (!editor.contains(task.node)) remove(task);
    },
    dispose() {
      disposed = true;
      window.removeEventListener('beforeunload', warnBeforeLeaving);
      for (const task of tasks.values()) task.abort.abort();
      tasks.clear();
      delete editor.dataset.inlineImagesPending;
    },
  };
}
